import { useState, useRef } from 'react';
import Papa from 'papaparse';
import { useData } from '../contexts/DataContext';
import { useTheme } from '../contexts/ThemeContext';
import ColumnMappingPanel from './ColumnMappingPanel';
import EditableDataTable from './EditableDataTable';
import LoadingSpinner from './LoadingSpinner';
import { getTemplate, templateToGrid, emptyGrid } from '../utils/dataTemplates';
import { inferTableStructure, detectDuplicates } from '../utils/tableInference';

// Infer column types: scan first 20 non-null values; >80% numeric → 'number'
// Also collects warnings about "almost numeric" columns for UX feedback.
function inferColumnTypes(rows, fields) {
  const sampleSize = Math.min(20, rows.length);
  const columns = [];
  const warnings = [];

  for (const name of fields) {
    let numericCount = 0;
    let validCount = 0;
    const offendingRows = [];
    for (let i = 0; i < sampleSize; i++) {
      const val = rows[i][name];
      if (val !== null && val !== undefined && String(val).trim() !== '') {
        validCount++;
        if (!isNaN(Number(val))) {
          numericCount++;
        } else {
          offendingRows.push({ rowIndex: i, value: String(val) });
        }
      }
    }
    const type = validCount > 0 && numericCount / validCount > 0.8 ? 'number' : 'string';
    columns.push({ name, type });

    if (type === 'string' && validCount > 0 && numericCount > 0 && numericCount / validCount >= 0.5) {
      warnings.push({
        column: name,
        numericCount,
        totalCount: validCount,
        offendingRows: offendingRows.slice(0, 3),
      });
    }
  }

  return { columns, warnings };
}

function detectEuropeanDecimals(grid) {
  const euroPattern = /^\d+,\d+$/;
  let euroCount = 0;
  let totalNumericLike = 0;
  const affectedCols = new Set();

  for (let r = 1; r < grid.length; r++) {
    for (let c = 0; c < grid[r].length; c++) {
      const val = String(grid[r][c]).trim();
      if (/^\d/.test(val)) totalNumericLike++;
      if (euroPattern.test(val)) {
        euroCount++;
        affectedCols.add(c);
      }
    }
  }
  return { hasEuroDecimals: euroCount > 0 && euroCount / Math.max(totalNumericLike, 1) > 0.3, euroCount, affectedCols: Array.from(affectedCols) };
}

function detectHeadersLookLikeData(headers) {
  if (!headers || headers.length === 0) return false;
  return headers.every(h => !isNaN(Number(h)) || h.trim() === '');
}

/**
 * Convert a 2D grid (header row + data rows) to parseable rows + columns.
 * Numeric-looking strings are cast to numbers.
 */
function gridToParsed(grid) {
  const fields = grid[0].filter(h => h.trim() !== '');
  const rows = grid.slice(1)
    .filter(row => row.some(cell => String(cell).trim() !== ''))
    .map(row => {
      const obj = {};
      fields.forEach((field, i) => {
        const raw = String(row[i] ?? '').trim();
        const num = Number(raw);
        obj[field] = raw !== '' && !isNaN(num) ? num : raw;
      });
      return obj;
    });
  return { fields, rows };
}

const STEPS = ['Import', 'Edit Table', 'Confirm Columns'];

function StepIndicator({ current, ui }) {
  return (
    <div className="flex items-center gap-0 flex-shrink-0">
      {STEPS.map((label, idx) => {
        const stepNum = idx + 1;
        const isActive = stepNum === current;
        const isDone = stepNum < current;
        return (
          <div key={label} className="flex items-center">
            <div
              className={`flex items-center gap-1.5 px-2 py-1 text-xs ${isActive ? 'font-medium' : ''}`}
              style={{ color: isActive ? ui.text : ui.textSecondary }}
            >
              <span
                className="w-4 h-4 flex items-center justify-center border text-[10px]"
                style={{
                  borderColor: isActive ? ui.border : ui.borderSecondary,
                  backgroundColor: isDone ? ui.bgSecondary : 'transparent',
                  fontWeight: isActive ? 500 : undefined,
                }}
              >
                {isDone ? '✓' : stepNum}
              </span>
              {label}
            </div>
            {idx < STEPS.length - 1 && (
              <span className="text-xs" style={{ color: ui.textSecondary }}>›</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DetectedFormatPanel({ inference, onUseDetected, onReviewColumns, onBack, ui }) {
  const mapping = inference?.suggestedMapping || {};
  const summaryParts = Object.entries(mapping)
    .filter(([key]) => !key.startsWith('_') && key !== 'inputShape')
    .map(([key, val]) => {
      if (Array.isArray(val)) return `${val.join(', ')} → plotted as separate lines`;
      return `"${val}"`;
    });

  const confidence = inference?.confidence;

  return (
    <div>
      <div
        className="border p-3 mb-4 text-xs leading-relaxed"
        style={{ backgroundColor: ui.bgSecondary, borderColor: ui.borderSecondary, color: ui.textSecondary }}
      >
        <p className="font-medium mb-1" style={{ color: ui.text }}>We auto-detected your table layout</p>
        <p>{inference?.notes || 'Your data looks ready to plot.'}</p>
        {summaryParts.length > 0 && (
          <p className="mt-1.5" style={{ color: ui.text }}>
            Columns used: {summaryParts.join(' · ')}
          </p>
        )}
        {confidence === 'medium' && (
          <p className="mt-1.5" style={{ color: '#b45309' }}>
            Not quite right? Click <strong>Adjust columns</strong> below to reassign which column maps where.
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={onBack}
          className="flex-1 text-xs border py-2 ui-hover ui-border"
          style={{ borderColor: ui.border, color: ui.text }}
        >
          ← Back
        </button>
        <button
          onClick={onReviewColumns}
          className="flex-1 text-xs border py-2 ui-hover ui-border"
          style={{ borderColor: ui.borderSecondary, color: ui.textSecondary }}
        >
          Adjust columns
        </button>
        <button
          onClick={onUseDetected}
          className="flex-1 text-xs border py-2 font-medium ui-hover ui-border"
          style={{ borderColor: ui.border, color: ui.text }}
        >
          Looks good — use this →
        </button>
      </div>
    </div>
  );
}

function DataImportModal({ onClose, selectedChartId }) {
  const { loadData, setMapping } = useData();
  const { resolved: theme } = useTheme();
  const ui = theme.ui || {};

  // step: 1 = source, 2 = table, 3 = mapping
  const [step, setStep] = useState(1);
  const [grid, setGrid] = useState(null);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef(null);

  // Parsed data for step 3
  const [parsedRows, setParsedRows] = useState(null);
  const [parsedColumns, setParsedColumns] = useState(null);
  const [parsedFileName, setParsedFileName] = useState('');
  const [inferenceResult, setInferenceResult] = useState(null);
  const [showManualMapping, setShowManualMapping] = useState(false);
  const [firstRowIsData, setFirstRowIsData] = useState(false);
  const [euroDecimalWarning, setEuroDecimalWarning] = useState(null);
  const [columnWarnings, setColumnWarnings] = useState([]);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [isParsing, setIsParsing] = useState(false);

  // ── Source handlers ──────────────────────────────────────────────────────

  const goToTable = (newGrid) => {
    setGrid(newGrid);
    setError('');
    setInferenceResult(null);
    setShowManualMapping(false);
    setStep(2);
  };

  const handleLoadTemplate = () => {
    const template = getTemplate(selectedChartId);
    goToTable(templateToGrid(template));
  };

  const handleDownloadTemplateCsv = () => {
    const chartId = selectedChartId || 'vertical-bar';
    const template = getTemplate(chartId);

    const metadataLines = [
      `# coplot_chart_type: ${chartId}`,
      `# instructions: ${template.instructions}`,
    ];

    const csvBody = Papa.unparse(template.rows, { columns: template.columns });
    const csv = `${metadataLines.join('\n')}\n${csvBody}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `coplot-example-${chartId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleStartBlank = () => {
    goToTable(emptyGrid(3, 5));
  };

  const handleParsedCSV = (results, fileName, treatFirstRowAsData) => {
    const rows = results.data;
    const fields = results.meta?.fields;
    if (!fields || fields.length === 0) {
      setError('Could not detect column headers. Make sure the first row is a header row.');
      return;
    }
    if (rows.length === 0) {
      setError('No data rows found.');
      return;
    }
    let dataRows = rows.map(row => fields.map(f => row[f] !== null && row[f] !== undefined ? String(row[f]) : ''));

    // Auto-trim trailing empty rows (common in Excel exports)
    while (dataRows.length > 0 && dataRows[dataRows.length - 1].every(c => c.trim() === '')) {
      dataRows.pop();
    }

    let finalHeaders = fields;
    if (treatFirstRowAsData) {
      finalHeaders = fields.map((_, i) => `Column ${i + 1}`);
      dataRows = [fields.map(String), ...dataRows];
    }

    const newGrid = [finalHeaders, ...dataRows];
    goToTable(newGrid);
    setParsedFileName(fileName);

    const euro = detectEuropeanDecimals(newGrid);
    setEuroDecimalWarning(euro.hasEuroDecimals ? euro : null);
  };

  const handleFileChange = (file, treatAsData) => {
    if (!file) return;
    setError('');
    setIsParsing(true);
    Papa.parse(file, {
      header: true,
      dynamicTyping: false,
      skipEmptyLines: false,
      comments: '#',
      complete: (results) => { setIsParsing(false); handleParsedCSV(results, file.name, treatAsData ?? firstRowIsData); },
      error: (err) => { setIsParsing(false); setError(`Parse error: ${err.message}`); },
    });
  };

  // Drag-and-drop
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange(file);
  };

  // ── Table → Mapping ──────────────────────────────────────────────────────

  const handleContinueToMap = () => {
    if (!grid) return;
    const { fields, rows } = gridToParsed(grid);
    if (fields.length === 0) { setError('Add at least one column header.'); return; }
    if (rows.length === 0) { setError('Add at least one data row.'); return; }
    const { columns, warnings } = inferColumnTypes(rows, fields);
    const inferred = inferTableStructure(rows, columns, selectedChartId);

    const dupInfo = inferred?.suggestedMapping
      ? detectDuplicates(rows, inferred.suggestedMapping, selectedChartId)
      : { hasDuplicates: false, duplicateGroups: [], totalDuplicateRows: 0 };

    setParsedRows(rows);
    setParsedColumns(columns);
    setInferenceResult(inferred);
    setColumnWarnings(warnings);
    setDuplicateInfo(dupInfo.hasDuplicates ? dupInfo : null);
    setError('');
    setShowManualMapping(!inferred?.suggestedMapping || inferred?.confidence === 'low');

    setStep(3);
  };

  const handleMappingConfirm = (mapping) => {
    loadData(parsedRows, parsedColumns, parsedFileName || 'data');
    setMapping(mapping);
    onClose();
  };

  // ── Misc ─────────────────────────────────────────────────────────────────

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const modalWidth = step === 1 ? 480 : step === 2 ? 660 : 660;

  const template = getTemplate(selectedChartId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: ui.overlayBackdrop }}
      onClick={handleBackdropClick}
    >
      <div
        className="border max-h-[90vh] flex flex-col ui-bg ui-border"
        style={{ width: modalWidth, backgroundColor: ui.bg, borderColor: ui.border }}
      >
        {/* Header */}
        <div
          className="h-12 px-4 border-b flex items-center justify-between flex-shrink-0 ui-border"
          style={{ borderBottomColor: ui.border }}
        >
          <StepIndicator current={step} ui={ui} />
          <button
            onClick={onClose}
            className="text-sm w-7 h-7 flex items-center justify-center border border-transparent ui-hover"
            style={{ color: ui.text }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-4 flex-1 overflow-y-auto flex flex-col gap-4">

          {/* ── Parsing spinner ── */}
          {isParsing && <LoadingSpinner message="Parsing CSV..." ui={ui} />}

          {/* ── Step 1: Source ── */}
          {step === 1 && !isParsing && (
            <>
              {/* Instructions */}
              <div
                className="border p-3 text-xs leading-relaxed ui-bg2 ui-border"
                style={{ backgroundColor: ui.bgSecondary, borderColor: ui.borderSecondary }}
              >
                <p className="font-medium mb-1" style={{ color: ui.text }}>Paste or upload your table as-is</p>
                <p style={{ color: ui.textSecondary }}>
                  Just make sure the first row has column headers. We'll auto-detect what goes where — no need
                  to restructure your data.
                </p>
                {template && (
                  <p className="mt-1.5" style={{ color: ui.textSecondary }}>
                    <span className="font-medium" style={{ color: ui.text }}>Tip: </span>
                    {template.instructions}
                  </p>
                )}
              </div>

              {/* Source options */}
              <div className="flex flex-col gap-2">
                {/* Upload CSV */}
                <div
                  className="border-2 border-dashed p-4 text-center cursor-pointer transition-colors"
                  style={{
                    borderColor: isDragging ? ui.border : ui.borderSecondary,
                    backgroundColor: isDragging ? ui.bgSecondary : 'transparent',
                  }}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onMouseEnter={e => {
                    if (!isDragging) e.currentTarget.style.borderColor = ui.border;
                    if (!isDragging) e.currentTarget.style.backgroundColor = ui.bgSecondary;
                  }}
                  onMouseLeave={e => {
                    if (!isDragging) e.currentTarget.style.borderColor = ui.borderSecondary;
                    if (!isDragging) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <p className="text-xs font-medium" style={{ color: ui.text }}>Upload CSV</p>
                  <p className="text-xs mt-0.5" style={{ color: ui.textSecondary }}>Click or drag a .csv file here</p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,.tsv,.txt,text/csv,text/plain"
                    className="hidden"
                    onChange={(e) => handleFileChange(e.target.files[0])}
                  />
                </div>

                {/* Load Template */}
                <button
                  onClick={handleLoadTemplate}
                  className="border text-xs py-2.5 font-medium ui-hover ui-border"
                  style={{ borderColor: ui.border, color: ui.text }}
                >
                  Load Template
                  <span className="font-normal ml-1" style={{ color: ui.textSecondary }}>
                    — pre-filled example for this chart type
                  </span>
                </button>

                {/* Download Example CSV */}
                <button
                  onClick={handleDownloadTemplateCsv}
                  className="border text-xs py-2.5 font-medium ui-hover ui-border"
                  style={{ borderColor: ui.border, color: ui.text }}
                >
                  Download Example CSV
                  <span className="font-normal ml-1" style={{ color: ui.textSecondary }}>
                    — includes chart type and formatting instructions
                  </span>
                </button>

                {/* First row is data toggle */}
                <label className="flex items-center gap-2 px-1 mt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={firstRowIsData}
                    onChange={(e) => setFirstRowIsData(e.target.checked)}
                    className="accent-current"
                  />
                  <span className="text-xs" style={{ color: ui.textSecondary }}>
                    My data doesn't have column headers
                    <span className="ml-1" style={{ color: ui.textSecondary, opacity: 0.7 }}>(first row is data, not labels)</span>
                  </span>
                </label>

                {/* Start Blank */}
                <button
                  onClick={handleStartBlank}
                  className="border text-xs py-2 ui-hover"
                  style={{ borderColor: ui.borderSecondary, color: ui.textSecondary }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = ui.border; e.currentTarget.style.color = ui.text; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = ui.borderSecondary; e.currentTarget.style.color = ui.textSecondary; }}
                >
                  Start with a blank table
                </button>
              </div>
            </>
          )}

          {/* ── Step 2: Edit table ── */}
          {step === 2 && grid && (
            <>
              {template?.editTip && (
                <div
                  className="border px-3 py-2 text-xs leading-relaxed"
                  style={{ backgroundColor: ui.bgSecondary, borderColor: ui.borderSecondary, color: ui.textSecondary }}
                >
                  <span className="font-medium" style={{ color: ui.text }}>Tip: </span>
                  {template.editTip}
                </div>
              )}

              {/* Header-looks-like-data hint */}
              {grid && detectHeadersLookLikeData(grid[0]) && !firstRowIsData && (
                <div
                  className="border px-3 py-2 text-xs leading-relaxed"
                  style={{ backgroundColor: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' }}
                >
                  <span className="font-medium">Heads up:</span> Your column headers look like data (they're all numbers).{' '}
                  <button
                    onClick={() => {
                      setFirstRowIsData(true);
                      const newHeaders = grid[0].map((_, i) => `Column ${i + 1}`);
                      const newGrid = [newHeaders, grid[0].map(String), ...grid.slice(1)];
                      setGrid(newGrid);
                    }}
                    className="underline font-medium"
                    style={{ color: '#92400e' }}
                  >
                    Treat first row as data instead
                  </button>
                </div>
              )}

              {/* European decimal warning */}
              {euroDecimalWarning && (
                <div
                  className="border px-3 py-2 text-xs leading-relaxed flex items-center justify-between"
                  style={{ backgroundColor: '#eff6ff', borderColor: '#93c5fd', color: '#1e40af' }}
                >
                  <div>
                    <span className="font-medium">European decimals detected:</span>{' '}
                    {euroDecimalWarning.euroCount} values use comma as decimal separator (e.g. "125,3").
                  </div>
                  <button
                    onClick={() => {
                      const newGrid = grid.map((row, ri) => {
                        if (ri === 0) return row;
                        return row.map((cell, ci) => {
                          if (euroDecimalWarning.affectedCols.includes(ci) && /^\d+,\d+$/.test(cell.trim())) {
                            return cell.replace(',', '.');
                          }
                          return cell;
                        });
                      });
                      setGrid(newGrid);
                      setEuroDecimalWarning(null);
                    }}
                    className="ml-3 px-3 py-1 border text-xs font-medium flex-shrink-0"
                    style={{ borderColor: '#3b82f6', color: '#1d4ed8', backgroundColor: '#dbeafe' }}
                  >
                    Convert all to "."
                  </button>
                </div>
              )}

              {/* Editable table */}
              <EditableDataTable
                grid={grid}
                onChange={setGrid}
              />

              {/* Actions */}
              <div className="flex gap-2 pt-1 flex-shrink-0">
                <button
                  onClick={() => { setStep(1); setError(''); setInferenceResult(null); setShowManualMapping(false); }}
                  className="flex-1 text-xs border py-2 ui-hover ui-border"
                  style={{ borderColor: ui.border, color: ui.text }}
                >
                  ← Back
                </button>
                <button
                  onClick={handleContinueToMap}
                  className="flex-1 text-xs border py-2 font-medium ui-hover ui-border"
                  style={{ borderColor: ui.border, color: ui.text }}
                >
                  Continue →
                </button>
              </div>
            </>
          )}

          {/* ── Step 3: Column mapping ── */}
          {step === 3 && parsedColumns && parsedRows && (
            <>
              {/* Duplicate row warning */}
              {duplicateInfo && (
                <div
                  className="border px-3 py-2.5 text-xs leading-relaxed"
                  style={{ backgroundColor: '#fef2f2', borderColor: '#fca5a5', color: '#991b1b' }}
                >
                  <div className="font-medium mb-1" style={{ fontSize: 13 }}>⚠ Duplicate rows detected</div>
                  <p className="mb-1.5">
                    Found {duplicateInfo.duplicateGroups.length} duplicate group{duplicateInfo.duplicateGroups.length > 1 ? 's' : ''} ({duplicateInfo.totalDuplicateRows} rows total).
                    Values with the same key will be <strong>summed</strong> in the chart, which may produce misleading results.
                  </p>
                  <div className="mb-2 pl-2 border-l-2" style={{ borderColor: '#fca5a5' }}>
                    {duplicateInfo.duplicateGroups.slice(0, 3).map((g, i) => (
                      <div key={i} className="text-[11px]" style={{ color: '#7f1d1d' }}>
                        "{g.key}" appears {g.count}× 
                      </div>
                    ))}
                    {duplicateInfo.duplicateGroups.length > 3 && (
                      <div className="text-[11px]" style={{ color: '#7f1d1d' }}>
                        ...and {duplicateInfo.duplicateGroups.length - 3} more
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { setStep(2); setError(''); setDuplicateInfo(null); }}
                    className="border px-3 py-1 text-xs font-medium"
                    style={{ borderColor: '#ef4444', color: '#dc2626', backgroundColor: '#fee2e2' }}
                  >
                    ← Go back and fix duplicates
                  </button>
                </div>
              )}

              {/* Column type warnings */}
              {columnWarnings.length > 0 && (
                <div
                  className="border px-3 py-2 text-xs leading-relaxed"
                  style={{ backgroundColor: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' }}
                >
                  <div className="font-medium mb-1">Column type notices</div>
                  {columnWarnings.map((w, i) => (
                    <div key={i} className="mb-1">
                      <strong>"{w.column}"</strong> was detected as text because{' '}
                      {w.totalCount - w.numericCount} of {w.totalCount} values are non-numeric
                      {w.offendingRows.length > 0 && (
                        <span> (e.g. row {w.offendingRows[0].rowIndex + 1}: "{w.offendingRows[0].value}")</span>
                      )}.{' '}
                      <button
                        onClick={() => { setStep(2); setError(''); }}
                        className="underline"
                        style={{ color: '#92400e' }}
                      >
                        Go back to fix
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {showManualMapping || !inferenceResult?.suggestedMapping ? (
                <ColumnMappingPanel
                  columns={parsedColumns}
                  rows={parsedRows}
                  selectedChartId={selectedChartId}
                  onConfirm={handleMappingConfirm}
                  onBack={() => { setStep(2); setError(''); setShowManualMapping(false); }}
                />
              ) : (
                <DetectedFormatPanel
                  inference={inferenceResult}
                  onUseDetected={() => handleMappingConfirm(inferenceResult.suggestedMapping)}
                  onReviewColumns={() => setShowManualMapping(true)}
                  onBack={() => { setStep(2); setError(''); }}
                  ui={ui}
                />
              )}
            </>
          )}

          {/* Error */}
          {error && (
            <p className="text-xs text-red-600 border border-red-300 bg-red-50 p-2 flex-shrink-0">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default DataImportModal;
